export const ASTER_MAX_SUPPLY = 250_000;
export const FATHER_WALLET_BALANCE = 40_000;
export const ASTER_GENESIS_PRICE = 1;
export const FORMULA_VERSION = "1.0";

export type ValuationInputs = {
  grossRevenue: number;
  taxes: number;
  operatingCosts: number;
  proLabore: number;
  profitDistribution: number;
  cash: number;
  debt: number;
  reserve: number;
  riskDiscount: number;
};

export type ValuationResult = {
  netRevenue: number;
  adjustedEbitda: number;
  revenueValue: number;
  ebitdaValue: number;
  operationalBase: number;
  conservativeOperationalValue: number;
  excessCash: number;
  valuation: number;
  price: number;
  walletValue: number;
};

export function calculateAsterValue(
  input: ValuationInputs,
): ValuationResult {
  const netRevenue = input.grossRevenue - input.taxes;
  const adjustedEbitda =
    netRevenue - input.operatingCosts - input.proLabore;
  const revenueValue = netRevenue;
  const ebitdaValue = adjustedEbitda * 4;
  const operationalBase = revenueValue * 0.5 + ebitdaValue * 0.5;
  const conservativeOperationalValue =
    Math.max(0, operationalBase) * (1 - input.riskDiscount);
  const excessCash = Math.max(0, input.cash - input.reserve);
  const valuation = Math.max(
    0,
    conservativeOperationalValue + excessCash - input.debt,
  );
  const price = valuation / ASTER_MAX_SUPPLY;

  return {
    netRevenue,
    adjustedEbitda,
    revenueValue,
    ebitdaValue,
    operationalBase,
    conservativeOperationalValue,
    excessCash,
    valuation,
    price,
    walletValue: price * FATHER_WALLET_BALANCE,
  };
}

export function toCents(value: number): number {
  return Math.round(value * 100);
}

export function fromCents(value: number | null | undefined): number {
  return (value ?? 0) / 100;
}

export function brl(value: number, decimals = 2): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

export function compactBrl(value: number): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

export function percent(value: number, decimals = 2): string {
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}

